import React from 'react';
import type { EpicDetail } from '../../../../../../shared/types/epics';

// ---------------------------------------------------------------------------
// Default Plan-tab content when there is nothing else to show.
//
// FORGE-348: a Parity Proof epic (`/parity-before`, state PARITY_*) has no
// story plan — its work happens in a Q&A on the GitHub issue and its results
// show on the Quality tab. Without this the tab was simply blank while the
// analyst was scanning, which read as broken. Other epics with no plan yet get
// a one-line "the plan appears here" note.
// ---------------------------------------------------------------------------

/** What the Parity Proof flow is doing right now, in plain language. */
export function parityStageText(state: string): { title: string; body: string } {
  switch (state) {
    case 'PARITY_SCOPING':
    case 'DEFINED':
      return {
        title: 'Scanning the repository',
        body: 'Forge is inventorying the solutions, projects and entry points. It will post the proposed scope — with a question list and defaults — as a comment on the GitHub issue. This usually takes 5–30 minutes.',
      };
    case 'PARITY_SCOPE_QUESTIONS':
      return {
        title: 'Waiting for your scope answers',
        body: 'The proposed scope is on the GitHub issue. Reply with the answers you want to change (for example "3: Billing" or "7: 80%"), or comment /proceed to accept the defaults.',
      };
    case 'PARITY_SCOPE_CONFIRMED':
    case 'PARITY_UNDERSTANDING_RUNNING':
    case 'PARITY_UNDERSTANDING_REVISION':
      return {
        title: 'Writing the Functional Understanding',
        body: 'Forge is describing what each functional area does and its business rules, with code references. It will be posted on the GitHub issue and on the Quality tab for your review.',
      };
    case 'PARITY_UNDERSTANDING_POSTED':
      return {
        title: 'Waiting for your review of the Functional Understanding',
        body: 'Review the areas and rules on the Quality tab or the GitHub issue. Reply with corrections (for example "BR-BIL-04: the cap is 15%"), or approve it to start test authoring.',
      };
    case 'PARITY_UNDERSTANDING_APPROVED':
    case 'PARITY_TESTS_AUTHORING':
      return {
        title: 'Writing the parity tests',
        body: 'Forge is writing tests/Forge.Parity — at least one test per approved rule. No product file is changed.',
      };
    case 'PARITY_BASELINE_RUNNING':
      return {
        title: 'Running the baseline on .NET Framework',
        body: 'The tests run twice on net48 (flaky ones are quarantined), with coverage and a mutation score per area. The Before report follows on the Quality tab.',
      };
    case 'PARITY_BASELINE_READY':
      return {
        title: 'Baseline ready',
        body: 'The Before report is on the Quality tab. Next: /migrate, then /parity-after re-runs the same tests on the migrated code.',
      };
    case 'PARITY_VERIFY_QUEUED':
    case 'PARITY_VERIFYING':
      return {
        title: 'Verifying parity',
        body: 'The frozen parity tests are re-running on the target runtime and being compared to the baseline. The After report follows on the Quality tab.',
      };
    case 'PARITY_VERIFIED':
    case 'PARITY_FAILED':
      return {
        title: state === 'PARITY_VERIFIED' ? 'Parity verified' : 'Parity failed',
        body: 'Per-area results, any divergences and the After report are on the Quality tab.',
      };
    case 'PARITY_GATE_BLOCKED':
      return {
        title: 'Waiting for a parity baseline',
        body: 'This migration needs a READY Parity Proof baseline before it can go to review. Run /parity-before, or waive with /parity waive <reason>.',
      };
    case 'PARITY_RUN_FAILED':
      return {
        title: 'The Parity Proof run stopped',
        body: 'The reason is in the latest comment on the GitHub issue. Nothing in the code was changed. Comment /parity-before (or /parity-after) to start again.',
      };
    default:
      return {
        title: 'Parity Proof',
        body: 'Progress is posted on the GitHub issue; results and reports appear on the Quality tab.',
      };
  }
}

export function isParityEpic(epic: EpicDetail): boolean {
  return epic.epicKind === 'parity-before' || String(epic.state ?? '').startsWith('PARITY_');
}

export const PlanEmptyState: React.FC<{ epic: EpicDetail }> = ({ epic }) => {
  if (isParityEpic(epic)) {
    const { title, body } = parityStageText(String(epic.state ?? ''));
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-card" data-testid="plan-parity-notice">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Parity Proof — no story plan</div>
        <h3 className="mt-1 text-base font-semibold text-slate-900">{title}</h3>
        <p className="mt-1 text-sm text-slate-600">{body}</p>
        <p className="mt-3 text-xs text-slate-500">
          A Parity Proof run doesn&apos;t use a story plan: the scope Q&amp;A happens on the GitHub issue
          {epic.github?.issueUrl ? (
            <> (<a className="text-primary-600 hover:underline" href={epic.github.issueUrl} target="_blank" rel="noreferrer">open issue</a>)</>
          ) : null}
          , and the Functional Understanding, test strength and Before/After reports appear on the <strong>Quality</strong> tab.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600" data-testid="plan-empty">
      No plan yet. The story plan appears here once the planner posts it on the GitHub issue and it is approved with /proceed.
    </div>
  );
};
